import { ApiResponse, Audience, User } from "@/types";
import {
  BuildAudienceApiResponse,
  DeleteAudienceApiResponse,
  GetAudienceApiResponse,
  GetAudiencesApiResponse,
  SaveAudienceApiResponse,
} from "./types";

import { AxiosRequestConfig } from "axios";
import { fetchData } from "@/utils";

export const getAudiences = async (
  accessToken: string,
  audienceType: string,
  client: string
): Promise<GetAudiencesApiResponse> => {
  let url = `${process.env.REACT_APP_API_URL}/v2/audience?audience_type=${audienceType}&client=${client}`;

  const config: AxiosRequestConfig = {
    method: "GET",
    url,
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  };

  return fetchData(config);
};

export const getAudience = async (
  accessToken: string,
  audienceId: string,
): Promise<GetAudienceApiResponse> => {
  let url = `${process.env.REACT_APP_API_URL}/v2/audience/${audienceId}`;

  const config: AxiosRequestConfig = {
    method: "GET",
    url,
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  };

  return fetchData(config);
};

export const deleteAudience = async (
  accessToken: string,
  name: string,
  id: string,
  type: string,
  client: string,
): Promise<DeleteAudienceApiResponse> => {
  const url = `${process.env.REACT_APP_API_URL}/v2/audience?audience_id=${id}&audience_name=${name}&audience_type=${type}&client=${client}`;

  const config: AxiosRequestConfig = {
    method: "DELETE",
    url,
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  };

  return fetchData(config);
};

export const buildAudience = async (
  accessToken: string,
  queryGroups: string[],
  threshold: number,
  operator: string,
  geoFence?: string,
  audienceId?: string,
): Promise<BuildAudienceApiResponse> => {
  // TODO pass down the full audience in a post request to give us more flexibility in the build

  let url = [
    `${process.env.REACT_APP_API_URL}/v2/build?query=${queryGroups.join("|")}`,
    `operator=${operator}`,
    `threshold=${threshold}`,
    geoFence ? `geo_fence=${geoFence}` : null,
    audienceId ? `audience_id=${audienceId}` : null,
  ].join("&");

  const config: AxiosRequestConfig = {
    method: "GET",
    url,
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  };

  return fetchData(config);
};

export const saveAudience = async (
  accessToken: string,
  audience: Audience,
  rebuild: boolean,
): Promise<SaveAudienceApiResponse> => {
  const url = `${process.env.REACT_APP_API_URL}/v2/audience?rebuild=${rebuild}`;

  const config: AxiosRequestConfig = {
    method: "POST",
    url,
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    data: audience,
  };

  return fetchData(config);
};

export const updateAudienceName = async (
  accessToken: string,
  audienceId: string | undefined,
  audienceName: string,
  projectName: string | null,
): Promise<GetAudienceApiResponse> => {
  const url = `${process.env.REACT_APP_API_URL}/v2/audience`;

  const data: Record<string, string | null> = {
    audience_id: audienceId ? audienceId : "",
    audience_name: audienceName,
    project_name: projectName,
  };

  const config: AxiosRequestConfig = {
    method: "PUT",
    url,
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    data,
  };

  return fetchData(config);
};

export const shareAudience = async (
  accessToken: string,
  audienceId: string,
  userIds: number[],
): Promise<
  ApiResponse<{
    message: string;
  }>
> => {
  const config: AxiosRequestConfig = {
    method: "POST",
    url: `${process.env.REACT_APP_API_URL}/audience/share`,
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    data: { id: audienceId, users: userIds },
  };

  return fetchData(config);
};

export const getUsers = async (
  accessToken: string,
): Promise<ApiResponse<User[]>> => {
  const config: AxiosRequestConfig = {
    method: "GET",
    url: `${process.env.REACT_APP_API_URL}/users`,
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
  };

  return fetchData(config);
};
